/*
=========================================================
* RingOperator Hero Section — Dispatch design
=========================================================
*/

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";

import MKBox from "components/MKBox";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";

import { brandPrimary, brandAccent, brandCream, brandCard } from "pages/RingOperatorHomepage/theme";
import Waveform from "pages/RingOperatorHomepage/sections/Waveform";
import useSignupUrl from "hooks/useSignupUrl";
import { getDisplayTrialDays } from "constants/promo";

const line = "rgba(14,13,11,0.1)";
const muted = "rgba(14,13,11,0.55)";

function FloatCard({ sx, delay, title, body, tag, html }) {
  return (
    <MKBox
      sx={{
        position: "absolute",
        background: brandCard,
        borderRadius: 2,
        border: `1px solid ${line}`,
        boxShadow: "0 20px 40px rgba(14,13,11,0.08)",
        p: "14px 16px",
        minWidth: 220,
        maxWidth: 260,
        animation: `ro-drop 700ms ease ${delay} backwards, ro-float 4s ease-in-out ${delay} infinite alternate`,
        ...sx,
      }}
    >
      <MKBox sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
        <MKTypography variant="body2" sx={{ fontSize: 13, fontWeight: 600, color: brandPrimary }}>
          {title}
        </MKTypography>
        {tag && (
          <MKBox
            component="span"
            sx={{
              fontSize: 11,
              background: brandAccent,
              color: "#fff",
              px: 1,
              py: "2px",
              borderRadius: 999,
              fontWeight: 600,
            }}
          >
            {tag}
          </MKBox>
        )}
      </MKBox>
      <MKTypography
        variant="body2"
        sx={{ fontSize: 12, color: muted, lineHeight: 1.45 }}
        dangerouslySetInnerHTML={{ __html: body || html }}
      />
    </MKBox>
  );
}

function HeroVisual() {
  return (
    <MKBox sx={{ position: "relative", aspectRatio: "1 / 1.05", minHeight: { xs: 380, md: 500 } }}>
      <MKBox
        sx={{
          position: "absolute",
          inset: 0,
          border: `1px dashed ${line}`,
          borderRadius: "50%",
          animation: "ro-orbit 60s linear infinite",
        }}
      />
      <MKBox
        sx={{
          position: "absolute",
          inset: 40,
          border: `1px dashed ${line}`,
          borderRadius: "50%",
          animation: "ro-orbit 40s linear infinite reverse",
        }}
      />
      <MKBox
        sx={{
          position: "absolute",
          inset: { xs: 70, md: 90 },
          background: brandCard,
          borderRadius: "50%",
          boxShadow: "0 30px 80px rgba(14,13,11,0.12), inset 0 0 0 1px rgba(14,13,11,0.06)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          p: 3,
          textAlign: "center",
        }}
      >
        <MKTypography
          className="ro-mono"
          sx={{
            fontSize: 11,
            color: muted,
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            mb: 1.5,
          }}
        >
          ● live
        </MKTypography>
        <MKBox sx={{ width: "65%", height: { xs: 80, md: 120 }, color: brandAccent }}>
          <Waveform bars={36} height={120} speed={1.2} variance={1.2} />
        </MKBox>
        <MKTypography
          className="ro-mono"
          sx={{ fontSize: 13, color: brandPrimary, fontWeight: 600, mt: 1.25 }}
        >
          call #4128 · 00:47
        </MKTypography>
      </MKBox>

      <FloatCard
        sx={{ top: "6%", left: "-6%" }}
        delay="0s"
        title="📅 Booking confirmed"
        body="Mike · Tue 2–4pm · HVAC repair"
        tag="+$340"
      />
      <FloatCard
        sx={{ top: "38%", right: "-8%" }}
        delay="0.6s"
        title="💬 SMS sent to caller"
        html="“We’re confirmed for Tuesday. Reply STOP to cancel.”"
      />
      <FloatCard
        sx={{ bottom: "4%", left: "2%" }}
        delay="1.2s"
        title="🌐 Booked from your page"
        body="Online booking · ringoperator.com/johnson-hvac"
      />
    </MKBox>
  );
}

function Hero() {
  const signupUrl = useSignupUrl();

  return (
    <MKBox
      component="section"
      className="ro-root"
      sx={{
        background: brandCream,
        py: { xs: 6, md: 10 },
        pt: { xs: 10, md: 14 },
        position: "relative",
      }}
    >
      <Container>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ mb: { xs: 4, md: 6 }, rowGap: 1 }}>
          <MKBox
            sx={{
              px: 1.75,
              py: 1,
              borderRadius: 999,
              background: brandCard,
              border: `1px solid ${line}`,
              fontSize: 13,
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <MKBox
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: brandAccent,
                boxShadow: `0 0 0 4px rgba(58,91,217,0.2)`,
              }}
            />
            Answers in 2 rings · 24/7
          </MKBox>
          <MKBox
            sx={{
              px: 1.75,
              py: 1,
              borderRadius: 999,
              border: `1px solid ${line}`,
              fontSize: 13,
              color: muted,
            }}
          >
            Speaks 30+ languages
          </MKBox>
        </Stack>

        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
          <Grid item xs={12} lg={7}>
            <MKTypography
              component="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "2.8rem", sm: "4rem", md: "5.5rem", lg: "7rem" },
                lineHeight: 0.9,
                letterSpacing: "-0.045em",
                color: brandPrimary,
                m: 0,
              }}
            >
              Your phone
              <br />
              <MKBox component="span" sx={{ position: "relative", display: "inline-block" }}>
                never sleeps.
                <MKBox
                  component="span"
                  sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: "0.08em",
                    height: "0.18em",
                    background: brandAccent,
                    zIndex: -1,
                    opacity: 0.3,
                    borderRadius: 1,
                  }}
                />
              </MKBox>
              <br />
              <MKBox component="span" className="ro-serif" sx={{ fontStyle: "italic" }}>
                Neither should you.
              </MKBox>
            </MKTypography>

            {/* Keyworded subhead. The H1 above is pure positioning and says
                nothing about what this is; this h2 is the rendered text that
                names the category people actually search for ("AI receptionist",
                "AI answering service") and the verticals we sell into. */}
            <MKTypography
              component="h2"
              sx={{
                color: brandPrimary,
                fontWeight: 500,
                fontSize: { xs: "1.15rem", md: "1.35rem" },
                lineHeight: 1.45,
                mt: 3,
                maxWidth: 560,
              }}
            >
              RingOperator is the AI receptionist for small businesses — a 24/7 AI answering
              service for salons, clinics, trades and every operator who can&apos;t stop to pick up.
            </MKTypography>

            <MKTypography
              sx={{
                color: muted,
                fontSize: { xs: "1.05rem", md: "1.2rem" },
                lineHeight: 1.55,
                mt: 2,
                maxWidth: 540,
              }}
            >
              It answers, books, and qualifies every call — in any language, at any hour — so you
              can actually get back to work.
            </MKTypography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 4 }}>
              <MKButton
                size="large"
                onClick={() => (window.location.href = signupUrl)}
                sx={{
                  background: `${brandPrimary} !important`,
                  color: "#fff !important",
                  px: 3,
                  py: 1.75,
                  borderRadius: 999,
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1.25,
                  "&:hover": { background: "#1A1915 !important" },
                }}
              >
                Start free · $0 for {getDisplayTrialDays()} days
                <MKBox
                  component="span"
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    background: brandAccent,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    color: "#fff",
                  }}
                >
                  →
                </MKBox>
              </MKButton>
            </Stack>

            <Stack direction="row" spacing={2.5} flexWrap="wrap" sx={{ mt: 3, rowGap: 1, fontSize: 13, color: muted }}>
              <span>✓ Setup in 30 minutes</span>
              <span>✓ Keep your number</span>
              <span>✓ Cancel anytime</span>
            </Stack>
          </Grid>

          <Grid item xs={12} lg={5}>
            <HeroVisual />
          </Grid>
        </Grid>
      </Container>
    </MKBox>
  );
}

export default Hero;
